/**
 * crmPermissions.ts
 *
 * Pure helper module for CRM permission checks.
 *
 * Design principles:
 * - No React imports; pure TypeScript so it can be tested in Vitest without
 *   a DOM environment.
 * - Mirrors the backend `hasPermission()` logic in
 *   `apps/api/src/auth/guards/permissions.guard.ts`.
 * - Permission strings follow the pattern `<resource>:<action>` where
 *   resource uses dot notation (e.g. `crm.customer`).
 * - Wildcards supported: `*:*`, `<resource>:*`, `*:<action>`.
 *
 * CRM permission constants are exported so callers never hard-code strings.
 */

// ─── CRM Permission Constants ─────────────────────────────────────────────────

export const CRM_PERMISSIONS = {
  CUSTOMER_CREATE: 'crm.customer:create',
  CUSTOMER_READ: 'crm.customer:read',
  CUSTOMER_UPDATE: 'crm.customer:update',

  FOLLOWUP_CREATE: 'crm.followup:create',
  FOLLOWUP_READ: 'crm.followup:read',
  FOLLOWUP_UPDATE: 'crm.followup:update',

  HANDOFF_CREATE: 'crm.handoff:create',
  HANDOFF_READ: 'crm.handoff:read',
  HANDOFF_UPDATE: 'crm.handoff:update',

  CHANNEL_PARTNER_CREATE: 'crm.channel_partner:create',
  CHANNEL_PARTNER_READ: 'crm.channel_partner:read',
  CHANNEL_PARTNER_UPDATE: 'crm.channel_partner:update',

  // Issue #345: CRM order operations live behind their own `crm.order:*`
  // permissions on the backend
  // (apps/api/src/modules/crm/controllers/order.controller.ts). The previous
  // OrderList implementation reused `crm.customer:update` which is the wrong
  // namespace — users with customer-only rights would have seen edit/archive
  // entries that the backend would 403-reject. These constants keep the UI
  // ergonomic guards aligned with the authoritative server gate.
  ORDER_CREATE: 'crm.order:create',
  ORDER_READ: 'crm.order:read',
  ORDER_UPDATE: 'crm.order:update',
  ORDER_FORCE_UPDATE: 'crm.order:force_update',

  ASSIGNMENT_READ: 'crm.assignment:read',
  ASSIGNMENT_UPDATE: 'crm.assignment:update',

  TAG_CREATE: 'crm.tag:create',
  TAG_READ: 'crm.tag:read',
  TAG_DELETE: 'crm.tag:delete',

  SOURCE_CREATE: 'crm.source:create',
  SOURCE_READ: 'crm.source:read',

  // CustomerAccount permissions (Issue #91)
  CUSTOMER_ACCOUNT_CREATE: 'crm.customer_account:create',
  CUSTOMER_ACCOUNT_READ: 'crm.customer_account:read',
  CUSTOMER_ACCOUNT_UPDATE: 'crm.customer_account:update',
  CUSTOMER_ACCOUNT_DELETE: 'crm.customer_account:delete',
} as const;

export type CrmPermission = (typeof CRM_PERMISSIONS)[keyof typeof CRM_PERMISSIONS];

// ─── Permission Checker ───────────────────────────────────────────────────────

/**
 * Check whether a flat list of granted permission strings satisfies a single
 * required permission.
 *
 * Mirrors the backend `hasPermission()` in permissions.guard.ts:
 *   - `<resource>:<action>`  — exact match
 *   - `<resource>:*`         — wildcard action
 *   - `*:<action>`           — wildcard resource
 *   - `*:*`                  — superuser
 *
 * Permission strings are stored as `resource:action` with colon separator.
 * The backend uses `@RequirePermission('crm.customer', 'read')` which results
 * in the permission string `crm.customer:read`.
 */
export function hasPermission(
  granted: readonly string[],
  required: string,
): boolean {
  const colonIdx = required.lastIndexOf(':');
  if (colonIdx === -1) {
    // Malformed permission string — treat as denied.
    return false;
  }
  const resource = required.slice(0, colonIdx);
  const action = required.slice(colonIdx + 1);

  const candidates = [
    `${resource}:${action}`,
    `${resource}:*`,
    `*:${action}`,
    '*:*',
  ];

  for (const c of candidates) {
    if (granted.includes(c)) {
      return true;
    }
  }
  return false;
}

/**
 * Returns true if the user has **any** of the provided permissions.
 * Use this for OR-semantics (e.g. "can see CRM menu if has any crm read perm").
 */
export function hasAnyPermission(
  granted: readonly string[],
  required: readonly string[],
): boolean {
  return required.some((p) => hasPermission(granted, p));
}

/**
 * Returns true if the user has **all** of the provided permissions.
 * Use this for AND-semantics.
 */
export function hasAllPermissions(
  granted: readonly string[],
  required: readonly string[],
): boolean {
  return required.every((p) => hasPermission(granted, p));
}

// ─── CRM Menu Visibility Helpers ─────────────────────────────────────────────

/**
 * Returns true if the CRM top-level sidebar entry should be visible.
 * The user needs at least one CRM read permission.
 *
 * Issue #345 audit fix3: include `crm.order:read` so a finance/boss role that
 * is provisioned only for CRM order oversight still sees the 客户与订单 menu
 * entry and can navigate to the orders sub-page; otherwise the top-level nav
 * would hide the entire CRM area even though the orders sub-page is gated on
 * `crm.order:read`.
 */
export function canSeeCrmMenu(granted: readonly string[]): boolean {
  return hasAnyPermission(granted, [
    CRM_PERMISSIONS.CUSTOMER_READ,
    CRM_PERMISSIONS.CHANNEL_PARTNER_READ,
    CRM_PERMISSIONS.ORDER_READ,
  ]);
}

/**
 * Returns true if the "客户管理" sub-nav tab should be visible.
 */
export function canSeeCustomerNav(granted: readonly string[]): boolean {
  return hasPermission(granted, CRM_PERMISSIONS.CUSTOMER_READ);
}

/**
 * Returns true if the "渠道合作方" sub-nav tab should be visible.
 */
export function canSeeChannelPartnerNav(granted: readonly string[]): boolean {
  return hasPermission(granted, CRM_PERMISSIONS.CHANNEL_PARTNER_READ);
}
